import PropTypes from 'prop-types';
import { React } from 'react';
import { MagnifyingGlassIcon, XCircleIcon } from '@heroicons/react/24/solid'; // Using solid icons
import DateRangePicker from './DateRangePicker';
import rawDataMessageTypeDefinition from '../../screens/Statistics/rawDataMessageTypeDefinition';
import MultiSelectDropdown from './MultiSelectDropdown'; // Import the new component

export default function FiltersWithMultiSelect({
  search, selectedStartDate, setSelectedStartDate, selectedStartTime,
  setSelectedStartTime, selectedEndDate, setSelectedEndDate,
  selectedEndTime, setSelectedEndTime,
  id, datesOptional, messageTypesVisible,
  deviceSerialVisible,
  linkVisible, linkText, linkURL,
  deviceSerial, setDeviceSerial,
  messageTypes, setMessageTypes,
  messageDirection, setMessageDirection, // Added messageDirection props
  deviceSerialError, maxDaysBetweenDates, subtitle,
  filtersComponents, setDateRangePickerVisibility,
  multiSelect,
}) {
  // Convert raw message type definitions to options for the dropdown
  const messageTypeOptions = Object.entries(rawDataMessageTypeDefinition).map(([messageTypeId, details]) => ({
    id: messageTypeId,
    label: `${messageTypeId} - ${details.description}`,
  }));

  // Helper to convert array to comma-separated string and vice-versa for multiselect
  const arrayToCsv = (arr) => arr.join(',');
  const csvToArray = (csv) => (csv ? csv.split(',') : []);

  return (
    <div className="p-4 sm:p-6 bg-white rounded-lg shadow space-y-6">
      {subtitle && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800">
            {subtitle}
          </h3>
        </div>
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-4 items-end">
        {filtersComponents && filtersComponents.map((component) => (
          component.type === 'radio' && (
            <div key={component.label} className="flex-grow min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">{component.label}</label>
              <div className="flex flex-wrap gap-x-4 gap-y-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                {component.values.map((value) => (
                  <label key={value.value} className="flex items-center text-sm text-gray-600 cursor-pointer">
                    <input
                      type="radio"
                      id={`btnRadio${id}${value.label}`}
                      name={`${id}-${component.label}`} // Ensure unique name for each radio group
                      value={value.value}
                      onChange={(e) => {
                        component.handleChange(e);

                      }}
                      defaultChecked={component.defaultValue === value.value}
                      className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="ml-2">{value.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )
        ))}

        <div className="flex-grow min-w-[280px]">
          <DateRangePicker
            selectedStartDate={selectedStartDate}
            setSelectedStartDate={setSelectedStartDate}
            selectedStartTime={selectedStartTime}
            setSelectedStartTime={setSelectedStartTime}
            selectedEndDate={selectedEndDate}
            setSelectedEndDate={setSelectedEndDate}
            selectedEndTime={selectedEndTime}
            setSelectedEndTime={setSelectedEndTime}
            datesOptional={datesOptional}
            maxDaysBetweenDates={maxDaysBetweenDates}
            setDateRangePickerVisibility={setDateRangePickerVisibility}
            onDateChange={(search) || (() => { })}
          />
        </div>

        {deviceSerialVisible && (
          <div className="flex-grow min-w-[200px]">
            <label htmlFor={`input${id}DeviceSerial`} className="block text-sm font-medium text-gray-700 mb-1">Device Serial</label>
            <input
              type="text"
              id={`input${id}DeviceSerial`}
              value={deviceSerial}
              onChange={(event) => {
                if (setDeviceSerial) {
                  setDeviceSerial(event.target.value);
                }
              }}
              className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Enter device serial"
            />
            {deviceSerialError && (
              <div className="mt-2 p-2 bg-red-50 border border-red-300 text-red-700 rounded-md text-xs flex items-center">
                <XCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                Cannot be empty
              </div>
            )}
          </div>
        )}

        {messageTypesVisible && (
          <div className="flex-grow min-w-[250px] w-full sm:w-auto md:max-w-xs">
            <div className="flex justify-between items-center mb-1">
              <label htmlFor={`input${id}MessageTypes`} className="block text-sm font-medium text-gray-700">Message Types</label>
            </div>
            {multiSelect ? (
              <MultiSelectDropdown
                options={messageTypeOptions}
                selectedIds={csvToArray(messageTypes)}
                onChange={(selectedIds) => setMessageTypes(arrayToCsv(selectedIds))}
                placeholder="Select type(s)"
              />
            ) : (
              <select
                id={`input${id}MessageTypes`}
                value={messageTypes} // Assuming single select stores value directly
                onChange={(event) => setMessageTypes(event.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white scrollbar scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
              >
                <option value="">
                  {messageTypes === "" ? "Select type" : "Clear Selection"}
                </option>
                {messageTypeOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Message Direction Dropdown */}
        <div className="flex-grow min-w-[200px]">
          <label htmlFor={`select${id}MessageDirection`} className="block text-sm font-medium text-gray-700 mb-1">Message Direction</label>
          <select
            id={`select${id}MessageDirection`}
            value={messageDirection}
            onChange={(event) => setMessageDirection(event.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
          >
            <option value="">Any</option>
            <option value="c2d">Cloud to Device (c2d)</option>
            <option value="d2c">Device to Cloud (d2c)</option>
          </select>
        </div>

        <div className="mt-auto self-end"> {/* Aligns button to the bottom of the flex line */}
          <button
            type="button"
            id={`btn${id}Search`}
            onClick={search}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            Search
          </button>
        </div>
      </div>

      {linkVisible && (
        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end items-center w-full">
          <a
            href={linkURL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            {linkText}
          </a>
        </div>
      )}
    </div>
  );
}

FiltersWithMultiSelect.propTypes = {
  search: PropTypes.func.isRequired,
  selectedStartDate: PropTypes.object, // Can be null if optional
  setSelectedStartDate: PropTypes.func.isRequired,
  selectedStartTime: PropTypes.string, // Can be empty
  setSelectedStartTime: PropTypes.func.isRequired,
  selectedEndDate: PropTypes.object, // Can be null if optional
  setSelectedEndDate: PropTypes.func.isRequired,
  selectedEndTime: PropTypes.string, // Can be empty
  setSelectedEndTime: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  deviceSerialVisible: PropTypes.bool.isRequired,
  linkVisible: PropTypes.bool,
  linkText: PropTypes.string,
  linkURL: PropTypes.string,
  messageTypesVisible: PropTypes.bool,
  datesOptional: PropTypes.bool,
  deviceSerial: PropTypes.string,
  setDeviceSerial: PropTypes.func,
  messageTypes: PropTypes.string, // Default to string for multiSelect
  setMessageTypes: PropTypes.func,
  messageDirection: PropTypes.string, // Added messageDirection prop type
  setMessageDirection: PropTypes.func, // Added setMessageDirection prop type
  deviceSerialError: PropTypes.bool,
  maxDaysBetweenDates: PropTypes.number,
  subtitle: PropTypes.string,
  filtersComponents: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    handleChange: PropTypes.func.isRequired,
    defaultValue: PropTypes.any,
    values: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    })).isRequired,
  })),
  setDateRangePickerVisibility: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]), // Keeping it flexible as original use was ambiguous
  multiSelect: PropTypes.bool,
};

FiltersWithMultiSelect.defaultProps = {
  linkVisible: false,
  linkText: '',
  linkURL: '',
  messageTypesVisible: false,
  datesOptional: true,
  deviceSerial: '',
  setDeviceSerial: () => { },
  messageTypes: '', // Default to empty string
  setMessageTypes: () => { },
  messageDirection: '', // Default to empty string
  setMessageDirection: () => { }, // Default to empty function
  deviceSerialError: false,
  maxDaysBetweenDates: 31,
  subtitle: '',
  filtersComponents: [],
  setDateRangePickerVisibility: false, // Or appropriate default if it's a function
  multiSelect: true,
  selectedStartDate: null,
  selectedStartTime: '',
  selectedEndDate: null,
  selectedEndTime: '',
};
